import _ from 'lodash'
import type {CreateExtArgs} from '@wix/document-manager-core'
import type {
    ComponentFeature,
    CompRef,
    DalValue,
    DependenciesManager,
    FragmentPointer,
    Fragments,
    FragmentsOptions,
    RawComponentExport,
    RawComponentExportStructure,
    RawImportExportOptions
} from '@wix/document-services-types'
import {
    STRUCTURE_PROPERTIES_TO_KEEP,
    BASE_STRUCTURE_PROPERTIES,
    COMPONENT_QUERIES,
    COMPONENT_QUERY_TO_NAMESPACE_MAP,
    SPECIAL_DATA_NODE_REFERENCE_MAP
} from '../constants'
import {updateChildren} from './utils/children'
import {updateComponentDataItem} from './utils/dataItems'
import {updateOverrides} from './utils/overrides'
import {getComponentFeaturesByType, isSystemDataItem} from '../utils'
import {updateGlobalData} from './utils/globalData'
import {updateFixerVersions} from './utils/fixerVersions'
import {getDependenciesManager} from './utils/dependencies'

const updateDataItems = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    component: RawComponentExportStructure,
    dependenciesManager: DependenciesManager,
    {componentFeatures}: RawImportExportOptions
) => {
    const {dal, pointers, extensionAPI} = extensionContext

    const features = getComponentFeaturesByType(component.structure.componentType, componentFeatures)
    _.forOwn(features, (feature: ComponentFeature) => {
        const {query, namespace, alias} = feature

        const dataItem = component.data[alias]

        updateComponentDataItem(extensionContext, componentPointer, dataItem, feature, dependenciesManager)

        const exportQueryValue = component.structure[query]
        if (isSystemDataItem(exportQueryValue, namespace, extensionAPI)) {
            const queryPointer = pointers.getInnerPointer(componentPointer, query)
            dal.set(queryPointer, exportQueryValue)
        }
    })
}

const updateComponentStructure = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    componentStructure: RawComponentExportStructure['structure']
) => {
    const {dal, extensionAPI} = extensionContext
    const origStructure = dal.get(componentPointer)
    const newStructure = {
        ..._.pick(origStructure, ...BASE_STRUCTURE_PROPERTIES),
        ...componentStructure,
        ..._.pickBy(origStructure, (value: DalValue, property: string) => {
            if (STRUCTURE_PROPERTIES_TO_KEEP.has(property)) {
                return true
            }

            if (COMPONENT_QUERIES.has(property)) {
                const namespace = COMPONENT_QUERY_TO_NAMESPACE_MAP[property]
                return !isSystemDataItem(value, namespace, extensionAPI)
            }
        })
    }

    if (origStructure.components === undefined) {
        delete newStructure.components
    }

    dal.set(componentPointer, newStructure)
}

const updateMobileStructure = ({dal, pointers}: CreateExtArgs, componentPointer: CompRef) => {
    const mobilePointer = pointers.structure.getMobilePointer(componentPointer)
    const origMobileStructure = dal.get(mobilePointer)
    if (origMobileStructure) {
        const componentStructure = dal.get(componentPointer)

        const newStructure = {
            ...componentStructure,
            components: []
        }

        if (origMobileStructure.components === undefined) {
            delete newStructure.components
        }

        if (origMobileStructure.components) dal.set(mobilePointer, newStructure)
    }
}

export const importAllComponents = (
    extensionContext: CreateExtArgs,
    componentId: RawComponentExport['rootComponent'],
    componentExport: RawComponentExport,
    componentToReplace: CompRef,
    dependenciesManager: DependenciesManager,
    options: RawImportExportOptions
) => {
    const {components} = componentExport
    const component = components[componentId]
    dependenciesManager.references.addReference(componentId, componentToReplace.type, {target: componentToReplace.id})

    updateComponentStructure(extensionContext, componentToReplace, component.structure)
    updateDataItems(extensionContext, componentToReplace, component, dependenciesManager, options)
    updateOverrides(extensionContext, componentToReplace, component, dependenciesManager, options)
    updateChildren(
        extensionContext,
        componentToReplace,
        component.structure,
        componentExport,
        dependenciesManager,
        options
    )
    updateMobileStructure(extensionContext, componentToReplace)
}

export const importRawComponent = (
    extensionContext: CreateExtArgs,
    componentId: RawComponentExport['rootComponent'],
    componentExport: RawComponentExport,
    componentToReplace: CompRef,
    options: RawImportExportOptions
) => {
    const {rootComponent, components, dependencies, globalData, fixerVersions} = componentExport
    const component = components[rootComponent]

    const dependenciesManager: DependenciesManager = getDependenciesManager(
        _.merge({}, {references: SPECIAL_DATA_NODE_REFERENCE_MAP}, dependencies)
    )

    updateGlobalData(extensionContext, componentToReplace, globalData, dependenciesManager)
    updateFixerVersions(extensionContext, componentToReplace, component, fixerVersions, dependenciesManager)
    importAllComponents(
        extensionContext,
        componentId,
        componentExport,
        componentToReplace,
        dependenciesManager,
        options
    )
}

export const replaceFragments = (
    extensionContext: CreateExtArgs,
    fragments: Fragments,
    options?: FragmentsOptions
): void => {
    const {dal, coreConfig} = extensionContext

    const isFragmentInstanceValid = (fragmentType: string, fragmentInstance: string) => {
        if (!coreConfig.supportedFragments?.[fragmentType]?.singletonInstance) {
            return true
        }
        return coreConfig.supportedFragments?.[fragmentType]?.singletonInstance === fragmentInstance
    }

    for (const [fragmentType, instances] of Object.entries(fragments)) {
        const allowedInstances = options?.[fragmentType]

        for (const [fragmentInstance, namespaces] of Object.entries(instances)) {
            if (allowedInstances?.length && !allowedInstances.includes(fragmentInstance)) {
                continue
            }

            if (!isFragmentInstanceValid(fragmentType, fragmentInstance)) {
                continue
            }

            for (const [type, items] of Object.entries(namespaces)) {
                for (const [id, item] of Object.entries(items)) {
                    const pointer: FragmentPointer = {
                        id,
                        type,
                        fragmentType,
                        fragmentInstance
                    }

                    dal.set(pointer, item)
                }
            }
        }
    }
}
